import { BusinessObjectBase } from './base';
import { measurementSchema } from '@schemas/business-objects/measurement';
import type { MeasurementSet } from './measurement-set';
import type { MeasurementType } from './measurement-type';
import type { Id, PixelCoordinate } from '../base-types';

export interface MeasurementData {
  readonly id: Id;
  readonly x1?: PixelCoordinate;
  readonly y1?: PixelCoordinate;
  readonly x2?: PixelCoordinate;
  readonly y2?: PixelCoordinate;
  readonly pixelLength?: number;
  readonly scaledLength?: number;
  readonly measurementSetId: Id;
  readonly measurementSet?: MeasurementSet;
  readonly measurementTypeId: Id;
  readonly measurementType?: MeasurementType;
}

export interface Measurement extends Readonly<MeasurementData> {}
export class Measurement extends BusinessObjectBase {
  static readonly entityName = 'Measurement' as const;
  static readonly plural = 'measurements';
  static schema = measurementSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Measurement[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Measurement(data));
  }

  static async findOne(filter?: any): Promise<Measurement | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Measurement(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Measurement> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Measurement(data);
  }

  static async create(data: Omit<MeasurementData, 'id'>): Promise<Measurement> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Measurement(result);
  }

  static async createMany(
    data: Omit<MeasurementData, 'id'>[],
  ): Promise<Measurement[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Measurement(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<MeasurementData>,
  ): Promise<Measurement> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Measurement(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
